import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface DeleteImageDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the image to delete.
   */
  imageId: string | null;
  /**
   * Callback when the image is deleted.
   */
  onDelete: (id: string) => Promise<void>;
  /**
   * Custom dialog title. Defaults to "Delete image from product page".
   */
  title?: string;
  /**
   * Custom dialog description. Defaults to "Are you sure want to permanently delete this image from your product page?".
   */
  description?: string;
}

function DeleteImageDialog(props: DeleteImageDialogProps) {
  const {
    isOpen = false,
    onOpenChange,
    onDelete,
    imageId,
    title = 'Delete image from product page',
    description = 'Are you sure want to permanently delete this image from your product page?',
    ...other
  } = props;
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleOnDelete = React.useCallback(async () => {
    if (imageId) {
      setIsDeleting(true);
      try {
        await onDelete?.(imageId);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [onDelete, imageId]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Sm} className="text-gray-utility-600">
              {description}
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={handleClose}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnDelete}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { DeleteImageDialog };
export type { DeleteImageDialogProps };
